import { usePermissionsStore } from "../permissions/permissions_store";
import { useState, useContext, useCallback, useMemo, useEffect } from "react";
import UIkit from "uikit";
import feather from "feather-icons";
import { AppContexts } from "../../providers";
import Tags from "../common/Tags";
import Properties from "../common/Properties";
import ReservationsService from "../../services/reservations.service";
import RentalsService from "../../services/rentals.service";
import CRMActivityPanel from "./activity-panel";
import CustomFieldsSection from "./custom-fields-section";
import {
  CRMSubscriptionRequired,
  hasCRMSubscription,
} from "../common/OpsSubscriptionRequired";
import usePropertyMacros from "../../utils/usePropertyMacros";
import {
  reservationMatchesPropertySearch,
  resolveReservationProperty,
} from "./reservation-utils";

const FeatherIcon = ({ name, size = 16, className = "" }) => {
  const icon = feather.icons[name];
  if (!icon) return null;
  return (
    <svg
      className={`feather-icon ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: icon.contents }}
    />
  );
};

const ACTIVITY_TYPE_ICONS = {
  note: "file-text",
  phone_call: "phone",
  text_message: "message-square",
  email: "mail",
  task: "check-circle",
  contact_created: "user-plus",
};

const CONTACT_TABS = [
  { key: "overview", label: "Overview" },
  { key: "reservations", label: "Reservations" },
  { key: "activity", label: "Activities" },
  { key: "tasks", label: "Tasks" },
  { key: "notes", label: "Notes" },
  { key: "events", label: "Events" },
];

const UNIT_TABS = [
  { key: "overview", label: "Overview" },
  { key: "reservations", label: "Reservations" },
  { key: "tasks", label: "Tasks" },
  { key: "notes", label: "Notes" },
];

const GUEST_TABS = [
  { key: "overview", label: "Overview" },
  { key: "reservations", label: "Reservations" },
  { key: "activity", label: "Activities" },
  { key: "tasks", label: "Tasks" },
  { key: "notes", label: "Notes" },
  { key: "events", label: "Events" },
];

const getInitials = (item) => {
  if (item?._type === "unit") return (item.pms_id || "U")[0].toUpperCase();
  const first = (item?.first_name || "")[0] || "";
  const last = (item?.last_name || "")[0] || "";
  return (first + last).toUpperCase() || "?";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const CRMDetail = ({
  contact,
  activeFilter,
  activeTab = "overview",
  onTabChange,
  onSendMessage,
  onEdit,
  onDelete,
  onRefresh,
  readOnly = false,
}) => {
  const { canEdit, canView } = usePermissionsStore();
  const crmContext = useContext(AppContexts.CRMContext);
  const { user } = useContext(AppContexts.AuthContext);
  const {
    updateContact,
    deleteContact,
  } = crmContext;

  const isUnit = contact?._type === "unit";
  const isGuest = contact?._type === "guest";
  const tabs = isUnit ? UNIT_TABS : isGuest ? GUEST_TABS : CONTACT_TABS;
  const effectiveActiveTab = tabs.some((tab) => tab.key === activeTab)
    ? activeTab
    : "overview";
  const crmSubscriptionLocked = !hasCRMSubscription(user);
  const isCRMSubscriptionTab = (tabKey) => {
    return ["activity", "notes", "events"].includes(tabKey);
  };

  useEffect(() => {
    if (effectiveActiveTab !== activeTab) {
      onTabChange?.(effectiveActiveTab);
    }
  }, [activeTab, effectiveActiveTab, onTabChange]);

  const handleEdit = useCallback(() => {
    if (!readOnly && canEdit("crm.contacts") && onEdit) onEdit();
  }, [onEdit, readOnly, canEdit]);

  const handleDelete = useCallback(() => {
    if (readOnly || !canEdit("crm.contacts")) return;
    if (onDelete) onDelete();
  }, [onDelete, readOnly, canEdit]);

  // Strip fields that don't exist on the contacts table before sending to backend
  const cleanContact = useCallback((c) => {
    const {
      _type,
      thread_id,
      total,
      activities,
      reservations,
      co_owners,
      status,
      formatted_phone,
      crm_identity_id,
      contact_id: _contactId,
      primary_role,
      display_name,
      phone_e164,
      ...rest
    } = c;
    // The CRM list mixes identity rows with contact rows. Ensure account_id
    // is always populated (the identity-based search doesn't include it).
    if (!rest.account_id && user?.account_id) {
      rest.account_id = user.account_id;
    }
    return rest;
  }, [user]);

  // Property CRUD
  const handlePropertiesChange = useCallback(
    (newRentals) => {
      const updated = cleanContact({ ...contact, rentals: newRentals });
      updateContact(updated);
    },
    [contact, updateContact, cleanContact],
  );

  // Tag CRUD
  const handleTagsChange = useCallback(
    (newTags) => {
      const updated = cleanContact({ ...contact, tags: newTags });
      updateContact(updated);
    },
    [contact, updateContact, cleanContact],
  );

  // Unit Tag CRUD
  const handleUnitTagsChange = useCallback(
    async (newTags) => {
      try {
        await RentalsService.update(contact.rental_id, { ...contact, tags: newTags });
        crmContext.updateItemInList(contact.id, { tags: newTags });
      } catch (err) {
        console.error("Failed to update unit tags", err);
      }
    },
    [contact, crmContext],
  );

  const handleGuestTagsChange = useCallback(
    async (newTags) => {
      // Guests may not always have a writable contact record; update UI optimistically.
      crmContext.updateItemInList(contact.id, { tags: newTags });
      try {
        if (contact.contact_id) {
          const updated = cleanContact({ ...contact, tags: newTags, id: contact.contact_id });
          await updateContact(updated);
        }
      } catch (err) {
        console.error("Failed to update guest tags", err);
      }
    },
    [contact, crmContext, cleanContact, updateContact],
  );

  const displayName = useMemo(() => {
    if (isUnit) return contact?.pms_id || contact?.name || "Unit";
    return `${contact?.first_name || ""} ${contact?.last_name || ""}`.trim() || "Unknown";
  }, [contact, isUnit]);

  const category = isUnit ? "unit" : (contact?.category || "other");

  const badgeText = useMemo(() => {
    if (isUnit) return "Unit";
    const cat = contact?.category || "other";
    return cat.charAt(0).toUpperCase() + cat.slice(1);
  }, [contact, isUnit]);

  return (
    <div className="crm-detail">
      {/* Header */}
      <div className="crm-detail-header">
        <div className={`crm-detail-avatar ${category}`}>
          {isUnit
            ? <span uk-icon="icon: home; ratio: 1.2"></span>
            : getInitials(contact)
          }
        </div>

        <div className="crm-detail-header-info">
          <h2 className="crm-detail-name">{displayName}</h2>
          {isUnit && contact?.address && (
            <div className="crm-detail-address">{contact.address}</div>
          )}
          {!isUnit && (
            <span className={`crm-detail-type-tag ${category}`}>
              {badgeText}
            </span>
          )}
          {!isUnit && contact?.created_at && (
            <div className="crm-detail-added">
              Added {formatDate(contact.created_at)}
            </div>
          )}
        </div>

        <div className="crm-detail-actions">
          {!isUnit && onSendMessage && (
            <button className="uk-button uk-button-default uk-button-small" onClick={onSendMessage} uk-tooltip="Send Message">
              <FeatherIcon name="message-square" size={15} />
            </button>
          )}
          {!isGuest && !readOnly && (
            <>
              <button className="uk-button uk-button-default uk-button-small" onClick={handleEdit} uk-tooltip="Edit">
                <FeatherIcon name="edit-2" size={15} />
              </button>
              <button className="uk-button uk-button-default uk-button-small" onClick={handleDelete} uk-tooltip="Delete">
                <FeatherIcon name="trash-2" size={15} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="crm-detail-tabs">
        {tabs.map((tab) => (
          <div
            key={tab.key}
            className={`crm-detail-tab ${effectiveActiveTab === tab.key ? "is-active" : ""}`}
            onClick={() => onTabChange?.(tab.key)}
            uk-tooltip={
              crmSubscriptionLocked && isCRMSubscriptionTab(tab.key)
                ? "CRM Subscription Required"
                : undefined
            }
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* Tab Content */}
      <div className="crm-detail-body">
        {effectiveActiveTab === "overview" && (
          isUnit
            ? <UnitOverviewTab contact={contact} onTagsChange={handleUnitTagsChange} readOnly={readOnly} />
            : isGuest
              ? <GuestOverviewTab contact={contact} onTagsChange={handleGuestTagsChange} readOnly={readOnly} />
              : <OverviewTab
                  contact={contact}
                  onPropertiesChange={handlePropertiesChange}
                  onTagsChange={handleTagsChange}
                  readOnly={readOnly}
                />
        )}
        {effectiveActiveTab === "activity" && canView("crm.activities") && (
          <CRMActivityPanel
            contact={contact}
            mode="activity"
            subscriptionLocked={crmSubscriptionLocked}
            lockedNotice={crmSubscriptionLocked ? <CRMSubscriptionRequired /> : null}
            readOnly={!canEdit("crm.activities")}
          />
        )}
        {effectiveActiveTab === "tasks" && canView("operations.tasks") && (
          <CRMActivityPanel contact={contact} mode="task" readOnly={!canEdit("operations.tasks")} />
        )}
        {effectiveActiveTab === "notes" && canView("crm.notes") && (
          <CRMActivityPanel
            contact={contact}
            mode="note"
            subscriptionLocked={crmSubscriptionLocked}
            lockedNotice={crmSubscriptionLocked ? <CRMSubscriptionRequired /> : null}
            readOnly={!canEdit("crm.notes")}
          />
        )}
        {effectiveActiveTab === "events" && canView("crm.activities") && (
          <CRMActivityPanel
            contact={contact}
            mode="event"
            subscriptionLocked={crmSubscriptionLocked}
            lockedNotice={crmSubscriptionLocked ? <CRMSubscriptionRequired /> : null}
            readOnly={!canEdit("crm.activities")}
          />
        )}
        {effectiveActiveTab === "reservations" && (
          isUnit
            ? <UnitReservationsTab contact={contact} />
            : <ReservationsTab contact={contact} />
        )}
      </div>
    </div>
  );
};

// ─── Unit Overview ───
const UnitOverviewTab = ({ contact, onTagsChange, readOnly = false }) => {
  const tags = contact?.tags || [];
  const { rows: macroRows, loadingListingText } = usePropertyMacros(contact?.rental_id);

  return (
    <>
      {/* Unit Info */}
      <div className="crm-section">
        <div className="crm-section-title">Unit Information</div>

        {contact?.pms_id && (
          <div className="crm-info-row">
            <span className="crm-info-label">PMS ID</span>
            <span className="crm-info-value">{contact.pms_id}</span>
          </div>
        )}

        {contact?.name && (
          <div className="crm-info-row">
            <span className="crm-info-label">Name</span>
            <span className="crm-info-value">{contact.name}</span>
          </div>
        )}

        {contact?.address && (
          <div className="crm-info-row">
            <span className="crm-info-label">Address</span>
            <span className="crm-info-value">{contact.address}</span>
          </div>
        )}

        {contact?.bedrooms != null && (
          <div className="crm-info-row">
            <span className="crm-info-label">Bedrooms</span>
            <span className="crm-info-value">{contact.bedrooms}</span>
          </div>
        )}

        {contact?.bathrooms != null && (
          <div className="crm-info-row">
            <span className="crm-info-label">Bathrooms</span>
            <span className="crm-info-value">{contact.bathrooms}</span>
          </div>
        )}
      </div>

      {/* Macros */}
      {(macroRows.length > 0 || loadingListingText) && (
        <div className="crm-section">
          <div className="crm-section-title">Macros</div>
          {loadingListingText && macroRows.length === 0 && (
            <div className="crm-empty-hint">Loading macro values…</div>
          )}
          {macroRows.map((row) => (
            <div key={row.id} className="crm-info-row">
              <span className="crm-info-label">{row.label}</span>
              <span className="crm-info-value">{row.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tags — with add/remove */}
      <div className="crm-section">
        <div className="crm-section-title">Tags</div>
        <div className="crm-detail-tags">
          <Tags
            value={tags}
            onChange={onTagsChange}
            readOnly={readOnly}
          />
        </div>
        {tags.length === 0 && (
          <div className="crm-empty-hint">No tags assigned</div>
        )}
      </div>

      {/* Custom Fields */}
      <CustomFieldsSection entityType="unit" entityId={contact?.rental_id} readOnly={readOnly} />
    </>
  );
};

// ─── Guest Overview ───
const GuestOverviewTab = ({ contact, onTagsChange, readOnly = false }) => {
  const tags = contact?.tags || [];
  return (
    <>
      <div className="crm-section">
        <div className="crm-section-title">Guest Information</div>

        {contact?.email && (
          <div className="crm-info-row">
            <span className="crm-info-label">Email</span>
            <span className="crm-info-value">{contact.email}</span>
          </div>
        )}

        {(contact?.phone || contact?.formatted_phone) && (
          <div className="crm-info-row">
            <span className="crm-info-label">Phone</span>
            <span className="crm-info-value">{contact.formatted_phone || contact.phone}</span>
          </div>
        )}
      </div>

      <div className="crm-section">
        <div className="crm-section-title">Tags</div>
        <div className="crm-detail-tags">
          <Tags
            value={tags}
            onChange={onTagsChange}
            readOnly={readOnly}
          />
        </div>
        {tags.length === 0 && (
          <div className="crm-empty-hint">No tags assigned</div>
        )}
      </div>

      {/* Custom Fields */}
      <CustomFieldsSection entityType="guest" entityId={contact?.guest_id || contact?.id} readOnly={readOnly} />
    </>
  );
};

// ─── Contact Overview ───
const OverviewTab = ({ contact, onPropertiesChange, onTagsChange, readOnly = false }) => {
  const properties = contact?.rentals || [];
  const coOwners = contact?.co_owners || [];
  const tags = contact?.tags || [];

  return (
    <>
      {/* Contact Info */}
      <div className="crm-section">
        <div className="crm-section-title">Contact Information</div>

        {contact?.first_name && (
          <div className="crm-info-row">
            <span className="crm-info-label">Name</span>
            <span className="crm-info-value">
              {contact.first_name} {contact.last_name || ""}
            </span>
          </div>
        )}

        {contact?.email && (
          <div className="crm-info-row">
            <span className="crm-info-label">Email</span>
            <span className="crm-info-value">{contact.email}</span>
          </div>
        )}

        {(contact?.phone || contact?.formatted_phone) && (
          <div className="crm-info-row">
            <span className="crm-info-label">Phone</span>
            <span className="crm-info-value">{contact.formatted_phone || contact.phone}</span>
          </div>
        )}
      </div>

      {/* Properties — with add/remove */}
      <div className="crm-section">
        <div className="crm-section-title">Properties</div>
        <div className="crm-detail-properties">
          <Properties
            value={properties}
            onChange={onPropertiesChange}
            readOnly={readOnly}
          />
        </div>
        {properties.length === 0 && (
          <div className="crm-empty-hint">No properties assigned</div>
        )}
      </div>

      {/* Co-Owners */}
      {coOwners.length > 0 && (
        <div className="crm-section">
          <div className="crm-section-title">Co-Owners</div>
          {coOwners.map((co, i) => (
            <div key={co?.id || i} className="crm-co-owner">
              <div className="crm-co-owner-avatar">
                {((co?.first_name || "")[0] || "") + ((co?.last_name || "")[0] || "")}
              </div>
              <div className="crm-co-owner-info">
                <div className="crm-co-owner-name">
                  {co?.first_name} {co?.last_name}
                </div>
                <div className="crm-co-owner-email">{co?.email}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tags — with add/remove */}
      <div className="crm-section">
        <div className="crm-section-title">Tags</div>
        <div className="crm-detail-tags">
          <Tags
            value={tags}
            onChange={onTagsChange}
            readOnly={readOnly}
          />
        </div>
        {tags.length === 0 && (
          <div className="crm-empty-hint">No tags assigned</div>
        )}
      </div>

      {/* Notes */}
      {contact?.notes && (
        <div className="crm-section">
          <div className="crm-section-title">Notes</div>
          <div className="crm-notes">{contact.notes}</div>
        </div>
      )}

      {/* Custom Fields */}
      <CustomFieldsSection
        entityType={contact?.category === "lead" || contact?.category === "leads" ? "lead" : "contact"}
        entityId={contact?.contact_id || contact?.id}
        readOnly={readOnly}
      />
    </>
  );
};

const ACTIVITY_PAGE_SIZE = 20;

const TimelineMessage = ({ content }) => {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useCallback((node) => {
    if (node) {
      setIsClamped(node.scrollHeight > node.clientHeight);
    }
  }, [content]);

  return (
    <>
      <div
        ref={textRef}
        className={`crm-timeline-text ${expanded ? "expanded" : "clamped"}`}
      >
        {content}
      </div>
      {isClamped && !expanded && (
        <button
          className="crm-timeline-show-more"
          onClick={() => setExpanded(true)}
        >
          Show more
        </button>
      )}
      {expanded && (
        <button
          className="crm-timeline-show-more"
          onClick={() => setExpanded(false)}
        >
          Show less
        </button>
      )}
    </>
  );
};

const ActivityTab = ({ contact, timeline, timelineLoading }) => {
  const [visibleCount, setVisibleCount] = useState(ACTIVITY_PAGE_SIZE);

  // Reset visible count when timeline changes
  useEffect(() => {
    setVisibleCount(ACTIVITY_PAGE_SIZE);
  }, [contact?.id, contact?.contact_id]);

  if (timelineLoading) {
    return (
      <div className="crm-list-empty">Loading timeline…</div>
    );
  }

  if (!timeline || timeline.length === 0) {
    return (
      <div className="crm-list-empty">No activity recorded yet</div>
    );
  }

  const getSenderLabel = (item) => {
    if (item.type === "contact_created") return "System";
    if (item.sender_type === "user") return item.sender_name || "You";
    if (item.sender_type === "recipient") {
      return `${contact?.first_name || ""} ${contact?.last_name || ""}`.trim() || "Contact";
    }
    if (item.sender_type === "automated") return "Automated";
    return item.sender_name || "";
  };

  const getDirectionClass = (item) => {
    if (item.sender_type === "recipient") return "inbound";
    if (item.sender_type === "user" || item.sender_type === "automated") return "outbound";
    return "";
  };

  const visibleTimeline = timeline.slice(0, visibleCount);
  const hasMore = visibleCount < timeline.length;

  return (
    <div className="crm-timeline">
      {visibleTimeline.map((item, i) => (
        <div key={item.id || i} className={`crm-timeline-item ${getDirectionClass(item)}`}>
          <div className="crm-timeline-line" />
          <div className={`crm-timeline-icon ${item.type || "note"}`}>
            <FeatherIcon name={ACTIVITY_TYPE_ICONS[item.type] || "bell"} size={14} />
          </div>
          <div className="crm-timeline-content">
            <div className="crm-timeline-header">
              {getSenderLabel(item) && (
                <span className="crm-timeline-sender">
                  {item.sender_type === "recipient" ? "← " : "→ "}
                  {getSenderLabel(item)}
                </span>
              )}
            </div>
            <TimelineMessage content={item.content} />
            <div className="crm-timeline-time">
              {formatDateTime(item.created_at)}
            </div>
          </div>
        </div>
      ))}
      {hasMore && (
        <div className="crm-timeline-load-more">
          <button
            className="uk-button uk-button-default uk-button-small"
            onClick={() => setVisibleCount((c) => c + ACTIVITY_PAGE_SIZE)}
          >
            Load more ({timeline.length - visibleCount} remaining)
          </button>
        </div>
      )}
    </div>
  );
};

const ReservationsTab = ({ contact }) => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const rentalContext = useContext(AppContexts.RentalsContext);

  useEffect(() => {
    const phone = contact?.formatted_phone || contact?.phone;
    setSearchTerm("");
    if (!phone) {
      setReservations([]);
      return;
    }
    setLoading(true);
    ReservationsService.getReservationByPhone(phone)
      .then((res) => {
        setReservations(res?.data || res || []);
      })
      .catch(() => setReservations([]))
      .finally(() => setLoading(false));
  }, [contact?.formatted_phone, contact?.phone]);

  const rentals = rentalContext?.rentals || [];
  const filteredReservations = reservations.filter((reservation) =>
    reservationMatchesPropertySearch(reservation, rentals, searchTerm),
  );

  const now = new Date();

  const categorize = (res) => {
    const checkIn = res.check_in ? new Date(res.check_in) : null;
    const checkOut = res.check_out ? new Date(res.check_out) : null;
    if (checkIn && checkOut && checkIn <= now && checkOut >= now) return "current";
    if (checkIn && checkIn > now) return "upcoming";
    return "past";
  };

  const grouped = { current: [], upcoming: [], past: [] };
  filteredReservations.forEach((r) => {
    const cat = categorize(r);
    grouped[cat].push(r);
  });

  const formatResDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const renderGroup = (label, items, icon) => {
    if (items.length === 0) return null;
    return (
      <div className="crm-section">
        <div className="crm-section-title">{label}</div>
        {items.map((res, i) => {
          const property = resolveReservationProperty(res, rentals);
          return (
            <div key={res?.guest_reservation_id || i} className="crm-reservation-card">
              <div className="crm-reservation-icon">
                <FeatherIcon name={icon} size={16} />
              </div>
              <div className="crm-reservation-info">
                <div className="crm-reservation-property">{property.primary}</div>
                {property.secondary && (
                  <div className="crm-reservation-address">{property.secondary}</div>
                )}
                <div className="crm-reservation-dates">
                  {formatResDate(res?.check_in)} – {formatResDate(res?.check_out)}
                </div>
                {res?.guest_name && (
                  <div className="crm-reservation-guest">{res.guest_name}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <div className="crm-list-empty">Loading reservations…</div>;
  }

  if (!reservations || reservations.length === 0) {
    return <div className="crm-list-empty">No reservations found</div>;
  }

  return (
    <div>
      <input
        className="uk-input uk-form-small crm-reservation-search"
        type="search"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        placeholder="Search by property name or address"
        aria-label="Search reservations by property name or address"
      />
      {filteredReservations.length === 0 && (
        <div className="crm-list-empty">No reservations match your search</div>
      )}
      {renderGroup("Current", grouped.current, "home")}
      {renderGroup("Upcoming", grouped.upcoming, "calendar")}
      {renderGroup("Past", grouped.past, "clock")}
    </div>
  );
};

const UnitReservationsTab = ({ contact }) => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const pmsId = contact?.pms_id;
    if (!pmsId) return;
    setLoading(true);
    ReservationsService.getReservationsByProperty(pmsId)
      .then((res) => {
        setReservations(res?.data || res || []);
      })
      .catch(() => setReservations([]))
      .finally(() => setLoading(false));
  }, [contact?.pms_id]);

  if (loading) {
    return <div className="crm-list-empty">Loading reservations…</div>;
  }

  if (!reservations || reservations.length === 0) {
    return <div className="crm-list-empty">No reservations found</div>;
  }

  const now = new Date();

  const categorize = (res) => {
    const checkIn = res.check_in ? new Date(res.check_in) : null;
    const checkOut = res.check_out ? new Date(res.check_out) : null;
    if (checkIn && checkOut && checkIn <= now && checkOut >= now) return "current";
    if (checkIn && checkIn > now) return "upcoming";
    return "past";
  };

  const grouped = { current: [], upcoming: [], past: [] };
  reservations.forEach((r) => {
    const cat = categorize(r);
    grouped[cat].push(r);
  });

  const formatResDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const guestName = (res) => {
    const name = `${res?.first_name || ""} ${res?.last_name || ""}`.trim();
    return name || "Unknown Guest";
  };

  const renderGroup = (label, items, icon) => {
    if (items.length === 0) return null;
    return (
      <div className="crm-section">
        <div className="crm-section-title">{label}</div>
        {items.map((res, i) => (
          <div key={res?.guest_reservation_id || i} className="crm-reservation-card">
            <div className="crm-reservation-icon">
              <FeatherIcon name={icon} size={16} />
            </div>
            <div className="crm-reservation-info">
              <div className="crm-reservation-property">
                {guestName(res)}
              </div>
              <div className="crm-reservation-dates">
                {formatResDate(res?.check_in)} – {formatResDate(res?.check_out)}
              </div>
              {res?.formatted_phone && (
                <div className="crm-reservation-guest">{res.formatted_phone}</div>
              )}
              {res?.email && (
                <div className="crm-reservation-guest">{res.email}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      {renderGroup("Current", grouped.current, "home")}
      {renderGroup("Upcoming", grouped.upcoming, "calendar")}
      {renderGroup("Past", grouped.past, "clock")}
    </div>
  );
};

export default CRMDetail;
